import React, { useState, useEffect } from 'react';
import { Upload, FileText, X, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaReportes() {
    const [file, setFile] = useState(null);
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [reportes, setReportes] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadReportes();
    }, []);

    const loadReportes = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/reportes`);
            const data = await response.json();
            setReportes(data.reportes || []);
        } catch (error) {
            console.error('Error loading reportes:', error);
            setReportes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!file || !titulo) {
            alert('Por favor complete el título y seleccione un archivo');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('titulo', titulo);
            formData.append('descripcion', descripcion);
            formData.append('archivo', file);

            const response = await fetch(`${API_URL}/jefatura/reportes`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                alert('Documento publicado exitosamente');
                setFile(null);
                setTitulo('');
                setDescripcion('');
                loadReportes();
            } else {
                const error = await response.json();
                alert(error.error || 'Error al publicar documento');
            }
        } catch (error) {
            console.error('Error uploading reporte:', error);
            alert('Error al publicar documento');
        } finally {
            setUploading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este documento?')) return;

        try {
            const response = await fetch(`${API_URL}/jefatura/reportes/${id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                alert('Documento eliminado exitosamente');
                loadReportes();
            } else {
                const error = await response.json();
                alert(error.error || 'Error al eliminar documento');
            }
        } catch (error) {
            console.error('Error deleting reporte:', error);
            alert('Error al eliminar documento');
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Comunicados</h1>
            </div>

            {/* Formulario de carga */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4">Adjuntar Documento para Estudiantes</h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Título del Documento <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Ej: Calendario Académico 2024"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                        <textarea
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows={2}
                            placeholder="Descripción breve del documento..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {file ? (
                                <div className="flex flex-col items-center gap-3">
                                    {file.type && file.type.startsWith('image/') ? (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            className="max-w-full max-h-48 rounded-lg border border-gray-200 object-contain"
                                        />
                                    ) : (
                                        <FileText className="text-blue-600" size={24} />
                                    )}
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-700 text-sm">{file.name}</span>
                                        <button onClick={() => setFile(null)} className="text-red-500 hover:text-red-700">
                                            <X size={20} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                                    <p className="text-gray-600 mb-2">Arrastra un archivo aquí o haz clic para seleccionar</p>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        id="file-upload"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="inline-block px-4 py-2 bg-blue-900 text-white rounded-full cursor-pointer hover:bg-blue-800"
                                    >
                                        Seleccionar archivo
                                    </label>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSubmit}
                            disabled={uploading || !file}
                            className={`px-6 py-2 rounded-full text-white ${uploading || !file ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'
                                }`}
                        >
                            {uploading ? 'Subiendo...' : 'Publicar Documento'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista de reportes */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                    <h3 className="font-semibold">Documentos Publicados</h3>
                </div>
                {loading ? (
                    <div className="px-6 py-8 text-center text-gray-500">Cargando...</div>
                ) : reportes.length === 0 ? (
                    <div className="px-6 py-8 text-center text-gray-500">No hay documentos publicados</div>
                ) : (
                    <div className="divide-y">
                        {reportes.map(r => {
                            const isImage = r.archivo_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(r.archivo_url);
                            const fileUrl = r.archivo_url ? (r.archivo_url.startsWith('http') ? r.archivo_url : `${API_URL.replace('/api/v1', '')}${r.archivo_url}`) : null;
                            return (
                                <div key={r.id_reporte} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-gray-800">{r.titulo}</p>
                                            <p className="text-sm text-gray-500">{r.descripcion || 'Sin descripción'} • {formatDate(r.fecha_publicacion)}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => handleDelete(r.id_reporte)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                title="Eliminar documento"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    {isImage && fileUrl && (
                                        <div className="mt-3">
                                            <img
                                                src={fileUrl}
                                                alt={r.titulo}
                                                className="max-w-full max-h-64 rounded-lg border border-gray-200 object-contain"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
